import { useState, useEffect } from "react";
import { toast } from "sonner";
import { apiGet, apiPut, apiPost } from "@/lib/shieldx";
import { PageHeader, Loader, Panel } from "@/components/shieldx/Primitives";
import { ActionDialog } from "@/components/shieldx/SuperAdmin";
import { Settings, Save } from "lucide-react";

const HIGH_IMPACT = new Set(["default_deny", "verification_required_for_protection", "require_mfa_for_privileged"]);
const BOOL_FIELDS = ["default_deny", "verification_required_for_protection", "require_mfa_for_privileged", "feature_attack_simulation", "feature_creator_guard"];
const NUM_FIELDS = [
  ["rate_limit_default", "Default Rate Limit (req/min)"],
  ["simulation_concurrency_limit", "Simulation Concurrency Limit"],
  ["min_security_rating_alert", "Min Security Rating Alert"],
  ["audit_retention_days", "Audit Retention (days)"],
];
const LABELS = {
  default_deny: "Default Deny (zero-trust)", verification_required_for_protection: "Require Verification for Protection",
  require_mfa_for_privileged: "Require MFA for Privileged Users", feature_attack_simulation: "Attack Simulation Module",
  feature_creator_guard: "Creator Guard Module",
};

export default function SAPlatformSettings() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [stepUp, setStepUp] = useState(null);

  const load = async () => { const c = await apiGet("/super-admin/settings"); setCfg(c); setDraft(c); };
  useEffect(() => { load(); }, []);
  if (!cfg) return <Loader label="Loading platform configuration" />;

  const changed = Object.keys(draft).filter((k) => k in cfg && draft[k] !== cfg[k] && k !== "versions");
  const hasHighImpact = changed.some((k) => HIGH_IMPACT.has(k));

  const doSave = async (extra = {}) => {
    const payload = {};
    changed.forEach((k) => { payload[k] = draft[k]; });
    await apiPut("/super-admin/settings", { ...payload, reason: extra.reason || "Settings update", step_up_token: extra.step_up_token });
    toast.success("Platform settings updated"); load();
  };
  const onSave = () => { if (hasHighImpact) setStepUp(true); else doSave(); };

  return (
    <div>
      <PageHeader icon={Settings} title="Platform Settings" subtitle="Global platform configuration. High-impact security settings require password re-authentication + reason and are version-tracked."
        actions={<button onClick={onSave} disabled={!changed.length} data-testid="settings-save"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"><Save className="h-4 w-4" /> Save {changed.length ? `(${changed.length})` : ""}</button>} />

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Security Policy" desc="High-impact toggles require step-up authentication.">
          <div className="space-y-3">
            {BOOL_FIELDS.map((k) => (
              <label key={k} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3">
                <div><p className="text-sm text-slate-200">{LABELS[k]}</p>{HIGH_IMPACT.has(k) && <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400/80">High impact</p>}</div>
                <button data-testid={`toggle-${k}`} onClick={() => setDraft((d) => ({ ...d, [k]: !d[k] }))}
                  className={`relative h-6 w-11 rounded-full transition-colors ${draft[k] ? "bg-blue-600" : "bg-slate-700"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${draft[k] ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </label>
            ))}
          </div>
        </Panel>
        <Panel title="Thresholds & Limits">
          <div className="space-y-3">
            {NUM_FIELDS.map(([k, label]) => (
              <div key={k}>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</label>
                <input data-testid={`num-${k}`} type="number" value={draft[k] ?? ""} onChange={(e) => setDraft((d) => ({ ...d, [k]: Number(e.target.value) }))}
                  className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50" />
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {cfg.versions?.length > 0 && (
        <Panel title="Change History" className="mt-5">
          <div className="space-y-2">
            {[...cfg.versions].reverse().slice(0, 10).map((v, i) => (
              <div key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2.5 text-xs">
                <div className="flex items-center justify-between"><span className="font-mono text-slate-400">{Object.keys(v.changes).join(", ")}</span><span className="text-slate-600">{new Date(v.ts).toLocaleString()}</span></div>
                <p className="mt-0.5 text-slate-500">By {v.by} · {v.reason || "—"}</p>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <ActionDialog open={!!stepUp} onClose={() => setStepUp(null)} action="SETTINGS_UPDATE" targetType="PLATFORM" targetId="global"
        title="Confirm high-impact settings change"
        body="One or more high-impact security settings changed. Re-authenticate to proceed." onDone={(e) => doSave(e)} />
    </div>
  );
}
